import React, { useState, useEffect, useRef } from 'react';
import './PublicNavbar.css';

const NAV_LINKS = [
  { id: 'home',    label: 'Home',    href: '#home' },
  { id: 'about',   label: 'About',   href: '#about' },
  {
    id: 'support',
    label: 'Student Support',
    href: '#',
    children: [
      { id: 'notices',       label: 'Notices',        href: 'notices',        icon: 'fa-solid fa-bell' },
      { id: 'question-papers', label: 'Question Papers', href: 'question-papers', icon: 'fa-solid fa-file-lines' },
      { id: 'blogs',         label: 'Blogs',          href: 'blogs',          icon: 'fa-solid fa-rss' },
    ],
  },
];

export default function PublicNavbar({ onLoginClick, onNavigate, currentView = 'home' }) {
  const [scrolled,     setScrolled]     = useState(false);
  const [mobileOpen,   setMobileOpen]   = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navRef      = useRef(null);

  /* ── Scroll detection ── */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  /* ── Smooth scroll ── */
  const handleNavClick = (e, href) => {
    if (href === '#') return;
    
    if (onNavigate) {
      if (href === '#home') {
        e.preventDefault();
        onNavigate('home');
        setMobileOpen(false);
        setDropdownOpen(false);
        window.scrollTo(0, 0);
        return;
      }
      
      if (!href.startsWith('#')) {
        e.preventDefault();
        onNavigate(href);
        setMobileOpen(false);
        setDropdownOpen(false);
        window.scrollTo(0, 0);
        return;
      }
    }

    e.preventDefault();
    
    // If we are currently on a dedicated sub-page and click a hash link, switch to home first
    if (currentView !== 'home' && href.startsWith('#')) {
      onNavigate?.('home');
      // Let it switch page, then scroll after a tiny delay
      setTimeout(() => {
        const target = document.querySelector(href);
        if (target) {
          const offset = 80;
          const top = target.getBoundingClientRect().top + window.scrollY - offset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
      }, 100);
    } else {
      const target = document.querySelector(href);
      if (target) {
        const offset = 80;
        const top = target.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }

    setMobileOpen(false);
    setDropdownOpen(false);
  };

  return (
    <nav className={`pub-nav ${scrolled ? 'pub-nav--scrolled' : ''} ${mobileOpen ? 'pub-nav--mobile-open' : ''}`} ref={navRef}>
      <div className="pub-nav__inner">

        {/* ── Logo ── */}
        <a href="#home" className="pub-nav__logo" onClick={(e) => handleNavClick(e, '#home')}>
          <img src="/logo.png" alt="Dr. SS Johl Library" />
        </a>

        {/* ── Desktop Links ── */}
        <ul className="pub-nav__links">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <li key={link.id} className="pub-nav__item pub-nav__item--dropdown" ref={dropdownRef}>
                <button
                  className={`pub-nav__link pub-nav__link--btn ${dropdownOpen ? 'pub-nav__link--active' : ''}`}
                  onClick={() => setDropdownOpen((o) => !o)}
                  aria-expanded={dropdownOpen}
                  aria-haspopup="true"
                >
                  {link.label}
                  <i className={`fa-solid fa-chevron-down pub-nav__chevron ${dropdownOpen ? 'pub-nav__chevron--open' : ''}`}></i>
                </button>

                {/* Dropdown Menu */}
                <div className={`pub-nav__dropdown ${dropdownOpen ? 'pub-nav__dropdown--open' : ''}`} role="menu">
                  <div className="pub-nav__dropdown-inner">
                    {link.children.map((child) => (
                      <a
                        key={child.id}
                        href={child.href}
                        className="pub-nav__dropdown-item"
                        role="menuitem"
                        onClick={(e) => handleNavClick(e, child.href)}
                      >
                        <span className="pub-nav__dropdown-icon">
                          <i className={child.icon}></i>
                        </span>
                        <span className="pub-nav__dropdown-label">{child.label}</span>
                        <i className="fa-solid fa-arrow-right pub-nav__dropdown-arrow"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </li>
            ) : (
              <li key={link.id} className="pub-nav__item">
                <a
                  href={link.href}
                  className="pub-nav__link"
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              </li>
            )
          )}
        </ul>

        {/* ── CTA ── */}
        <div className="pub-nav__cta">
          <button className="pub-nav__login-btn" id="navbar-login-btn" onClick={onLoginClick}>
            <i className="fa-solid fa-right-to-bracket"></i>
            <span>Login</span>
          </button>
        </div>

        {/* ── Hamburger ── */}
        <button
          className={`pub-nav__hamburger ${mobileOpen ? 'pub-nav__hamburger--open' : ''}`}
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle navigation"
          id="navbar-hamburger"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>

      {/* ── Mobile Menu ── */}
      <div className={`pub-nav__mobile-menu ${mobileOpen ? 'pub-nav__mobile-menu--open' : ''}`}>
        <ul className="pub-nav__mobile-links">
          {NAV_LINKS.map((link) =>
            link.children ? (
              <li key={link.id} className="pub-nav__mobile-item">
                <button
                  className="pub-nav__mobile-link pub-nav__mobile-link--parent"
                  onClick={() => setDropdownOpen((o) => !o)}
                >
                  {link.label}
                  <i className={`fa-solid fa-chevron-down ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>
                <div className={`pub-nav__mobile-sub ${dropdownOpen ? 'pub-nav__mobile-sub--open' : ''}`}>
                  {link.children.map((child) => (
                    <a
                      key={child.id}
                      href={child.href}
                      className="pub-nav__mobile-sublink"
                      onClick={(e) => handleNavClick(e, child.href)}
                    >
                      <i className={child.icon}></i>
                      {child.label}
                    </a>
                  ))}
                </div>
              </li>
            ) : (
              <li key={link.id} className="pub-nav__mobile-item">
                <a
                  href={link.href}
                  className="pub-nav__mobile-link"
                  onClick={(e) => handleNavClick(e, link.href)}
                >
                  {link.label}
                </a>
              </li>
            )
          )}
          <li className="pub-nav__mobile-item">
            <button className="pub-nav__mobile-login" onClick={() => { onLoginClick?.(); setMobileOpen(false); }}>
              <i className="fa-solid fa-right-to-bracket"></i> Login
            </button>
          </li>
        </ul>
      </div>
    </nav>
  );
}
